import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Mail, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Group users by customer_code
      const usersWithCode = response.data.filter(u => u.customer_code);
      const grouped = {};

      usersWithCode.forEach(user => {
        const code = user.customer_code;
        if (!grouped[code]) {
          grouped[code] = {
            customer_code: code,
            business_name: user.business_name,
            users: []
          };
        }
        grouped[code].users.push(user);
      });

      // Convert to array and sort by code
      const accountsArray = Object.values(grouped).sort((a, b) => 
        a.customer_code.localeCompare(b.customer_code)
      );

      setAccounts(accountsArray);
    } catch (error) {
      toast.error('Error al cargar cuentas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (code) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(code)) {
      newExpanded.delete(code);
    } else {
      newExpanded.add(code);
    }
    setExpandedAccounts(newExpanded);
  };

  if (loading) {
    return <div className="text-center py-8">Cargando cuentas...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-bold text-slate-900 mb-2">
          📊 Gestión de Cuentas por Código de Cliente
        </h3>
        <p className="text-sm text-slate-600">
          Un mismo código de cliente puede tener múltiples usuarios (emails) asociados.
          Todos los usuarios con el mismo código comparten la misma cuenta comercial.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
        <div className="mb-4 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            Total Cuentas: {accounts.length}
          </h3>
          <p className="text-sm text-slate-600">
            {accounts.reduce((sum, acc) => sum + acc.users.length, 0)} usuarios totales
          </p>
        </div>

        {accounts.length === 0 ? (
          <p className="text-center py-8 text-slate-500">
            No hay cuentas con código de cliente asignado
          </p>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => {
              const isExpanded = expandedAccounts.has(account.customer_code);
              const hasMultipleUsers = account.users.length > 1;

              return (
                <div
                  key={account.customer_code}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Account Header */}
                  <div
                    className={`p-4 ${hasMultipleUsers ? 'bg-slate-50 cursor-pointer hover:bg-slate-100' : 'bg-white'} transition-colors`}
                    onClick={() => hasMultipleUsers && toggleExpand(account.customer_code)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="bg-blue-600 text-white px-3 py-2 rounded-lg font-bold text-sm">
                          {account.customer_code}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Building2 className="w-4 h-4 text-slate-500" />
                            <span className="font-semibold text-slate-900">
                              {account.business_name}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {account.users.length} {account.users.length === 1 ? 'usuario' : 'usuarios'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {hasMultipleUsers && (
                        <div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Show first user if only one, or if not expanded */}
                    {(!hasMultipleUsers || !isExpanded) && (
                      <div className="mt-3 pl-4 border-l-2 border-blue-200">
                        <div className="flex items-center space-x-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{account.users[0].email}</span>
                        </div>
                        {account.users[0].contact_name && (
                          <div className="text-xs text-slate-500 mt-1">
                            👤 {account.users[0].contact_name}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded Users List */}
                  {hasMultipleUsers && isExpanded && (
                    <div className="bg-white border-t border-slate-200 p-4">
                      <div className="space-y-3">
                        {account.users.map((user, idx) => (
                          <div
                            key={user.email}
                            className="pl-4 border-l-2 border-blue-200 py-2"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-slate-700">
                                    Usuario {idx + 1}:
                                  </span>
                                  <Mail className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm text-slate-700">
                                    {user.email}
                                  </span>
                                </div>
                                {user.contact_name && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    👤 {user.contact_name}
                                  </div>
                                )}
                                {user.phone && (
                                  <div className="text-xs text-slate-500">
                                    📞 {user.phone}
                                  </div>
                                )}
                              </div>
                              <div>
                                {user.approved ? (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                                    Aprobado
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                                    Pendiente
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsManager;
